<template>
  <div class="flex flex-col gap-4">
    <div v-for="field in textFields" :key="field.key">
      <div class="mb-1 flex justify-between text-label text-stone-800">
        <label :for="`${id}-${field.key}`">{{ field.label }}</label>
        <span
          :data-test-id="`giftcard-${field.key}-counter`"
          class="text-stone-500"
          aria-live="polite"
        >
          {{ (modelValue[field.key] ?? '').length }} / {{ field.max }}
        </span>
      </div>
      <component
        :is="field.multiline ? 'textarea' : 'input'"
        :id="`${id}-${field.key}`"
        :data-test-id="`giftcard-${field.key}`"
        :rows="field.multiline ? 3 : undefined"
        :type="field.multiline ? undefined : 'text'"
        :maxlength="field.max"
        class="w-full rounded-lg border border-secondary px-4 outline-hidden ease duration-300 focus:border-primary-light"
        :class="field.multiline ? 'py-3' : 'h-[42px]'"
        :value="modelValue[field.key]"
        @input="update(field.key, $event)"
      />
    </div>

    <div class="flex flex-col md:flex-row gap-4">
      <div v-for="field in nameFields" :key="field.key" class="flex-1">
        <label
          :for="`${id}-${field.key}`"
          class="mb-1 inline-flex text-label text-stone-800"
        >
          {{ field.label }}
        </label>
        <input
          :id="`${id}-${field.key}`"
          type="text"
          :data-test-id="field.testId"
          class="h-[42px] w-full rounded-lg border border-secondary px-4 outline-hidden ease duration-300 focus:border-primary-light"
          :placeholder="field.placeholder"
          :value="modelValue[field.key]"
          maxlength="20"
          @input="update(field.key, $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
export interface GiftcardMessage {
  title?: string
  message?: string
  buyerName?: string
  recipientName?: string
}

const GIFTCARD_TITLE_MAX_LENGTH = 40
const GIFTCARD_MESSAGE_MAX_LENGTH = 120

const { t } = useI18n()
const id = useId()

const { modelValue } = defineProps<{
  modelValue: GiftcardMessage
}>()

const emit = defineEmits<{
  'update:modelValue': [value: GiftcardMessage]
}>()

const textFields = computed(() => [
  {
    key: 'title' as const,
    label: t('personalization.theme.message.titleLabel'),
    max: GIFTCARD_TITLE_MAX_LENGTH,
    multiline: false,
  },
  {
    key: 'message' as const,
    label: t('personalization.theme.message.messageLabel'),
    max: GIFTCARD_MESSAGE_MAX_LENGTH,
    multiline: true,
  },
])

const nameFields = computed(() => [
  {
    key: 'buyerName' as const,
    label: t('personalization.delivery.fromLabel'),
    placeholder: t('personalization.delivery.fromPlaceholder'),
    testId: 'giftcard-buyer-name',
  },
  {
    key: 'recipientName' as const,
    label: t('personalization.delivery.toLabel'),
    placeholder: t('personalization.delivery.toPlaceholder'),
    testId: 'giftcard-recipient-name',
  },
])

const update = (key: keyof GiftcardMessage, event: Event) => {
  const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value
  emit('update:modelValue', { ...modelValue, [key]: value })
}
</script>
